<template>
  <v-app>
    <v-container
      fluid
      class="pa-4 pa-md-8"
      style="max-width: 900px; margin: 0 auto;"
    >
      <v-row
        justify="center"
        class="mb-6"
      >
        <v-col
          cols="12"
          class="text-center"
        >
          <img
            src="@/assets/images/logos/chess-hotel.png"
            alt="CHESS HOTEL"
            style="max-width: 320px;"
          >
        </v-col>
      </v-row>

      <v-row
        justify="center"
        class="mb-4"
      >
        <v-col
          cols="12"
          class="text-center"
        >
          <v-btn-toggle
            v-model="locale"
            mandatory
            dense
            group
            @change="setLocale"
          >
            <v-btn
              value="pt"
              small
            >
              🇧🇷 PT
            </v-btn>
            <v-btn
              value="en"
              small
            >
              🇺🇸 EN
            </v-btn>
            <v-btn
              value="es"
              small
            >
              🇪🇸 ES
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-stepper
        v-model="step"
        elevation="4"
      >
        <v-stepper-header>
          <v-stepper-step
            :complete="step > 1"
            step="1"
          >
            {{ $t('preCheckIn.steps.reserve') }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step
            :complete="step > 2"
            step="2"
          >
            {{ $t('preCheckIn.steps.guests') }}
          </v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="3">
            {{ $t('preCheckIn.steps.confirmation') }}
          </v-stepper-step>
        </v-stepper-header>

        <!-- ── STEP 1: Número da reserva ─────────────────────── -->
        <v-stepper-content step="1">
          <v-card
            flat
            class="pa-4"
          >
            <v-card-title class="justify-center text-h6 mb-2">
              {{ $t('preCheckIn.step1.title') }}
            </v-card-title>
            <v-card-subtitle class="text-center mb-4">
              {{ $t('preCheckIn.step1.subtitle') }}
            </v-card-subtitle>
            <v-row justify="center">
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="reserveId"
                  outlined
                  :label="$t('preCheckIn.step1.reserveNumber')"
                  type="number"
                  :error-messages="reserveError"
                  @input="reserveError = ''"
                  @keyup.enter="loadReserve"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  x-large
                  :loading="loadingReserve"
                  @click="loadReserve"
                >
                  {{ $t('preCheckIn.step1.continue') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-stepper-content>

        <!-- ── STEP 2: Hóspedes ──────────────────────────────── -->
        <v-stepper-content step="2">
          <v-card
            v-if="reserve"
            flat
            class="pa-2 pa-md-4"
          >
            <v-alert
              type="info"
              outlined
              class="mb-4"
              dense
            >
              <strong>{{ $t('preCheckIn.step2.reservationLabel') }} #{{ reserve.id }}</strong> —
              {{ $t('preCheckIn.step2.checkIn') }}: {{ formatDate(reserve.check_in) }} |
              {{ $t('preCheckIn.step2.checkOut') }}: {{ formatDate(reserve.check_out) }}
              <span v-if="reserve.category"> | {{ reserve.category }}</span>
            </v-alert>

            <v-alert
              v-if="reserve.pre_checkin_done"
              type="success"
              outlined
              dense
              class="mb-4"
              icon="mdi-check-circle"
            >
              <strong>{{ $t('preCheckIn.step2.alreadyDoneTitle') }}</strong>
              {{ $t('preCheckIn.step2.alreadyDoneBody') }}
            </v-alert>

            <v-alert
              v-else
              type="info"
              text
              dense
              class="mb-4"
            >
              {{ $t('preCheckIn.step2.progress', { completed: reserve.completed_guests, total: reserve.total_guests }) }}
            </v-alert>

            <!-- Regras do Hotel -->
            <div
              v-if="reserve.rules && reserve.rules.length"
              class="mb-4"
            >
              <div class="d-flex align-center mb-2">
                <v-icon
                  color="orange darken-2"
                  class="mr-2"
                >
                  mdi-shield-alert-outline
                </v-icon>
                <span class="subtitle-2 font-weight-bold">{{ $t('preCheckIn.step2.hotelRules') }}</span>
              </div>
              <div
                v-for="(rule, i) in reserve.rules"
                :key="i"
                class="rule-card mb-3 pa-3 d-flex align-start"
                :class="rule.icon === 'smoking' ? 'rule-smoking' : 'rule-info'"
              >
                <div class="rule-icon-wrap mr-3 flex-shrink-0">
                  <v-icon
                    size="28"
                    :color="rule.icon === 'smoking' ? 'red darken-2' : 'blue darken-1'"
                  >
                    {{ rule.icon === 'smoking' ? 'mdi-smoking-off' : 'mdi-information' }}
                  </v-icon>
                </div>
                <div
                  class="body-2"
                  style="line-height:1.5;"
                  v-html="nl2br(rule.text)"
                ></div>
              </div>
            </div>

            <!-- Tabs por hóspede -->
            <v-tabs
              v-model="activeTab"
              show-arrows
              class="mb-1"
            >
              <v-tab
                v-for="(guest, i) in guests"
                :key="i"
              >
                <v-icon
                  small
                  class="mr-1"
                  :color="guest.status === 'completed' ? 'success' : undefined"
                >
                  {{ guest.status === 'completed' ? 'mdi-check-circle' : (guest.type === 'child' ? 'mdi-account-child' : 'mdi-account') }}
                </v-icon>
                {{ guestLabel(guest, i) }}
              </v-tab>
            </v-tabs>
            <v-divider class="mb-4"></v-divider>

            <!-- Formulário do hóspede ativo -->
            <v-tabs-items v-model="activeTab">
              <v-tab-item
                v-for="(guest, tabIdx) in guests"
                :key="tabIdx"
              >
                <v-card
                  v-if="isTabLocked(tabIdx)"
                  flat
                  outlined
                  class="pa-6 text-center"
                >
                  <v-icon
                    color="success"
                    size="56"
                    class="mb-2"
                  >
                    mdi-check-circle
                  </v-icon>
                  <div class="text-subtitle-1 font-weight-bold">
                    {{ guest.name || guestLabel(guest, tabIdx) }}
                  </div>
                  <div class="body-2 grey--text mb-4">
                    {{ $t('preCheckIn.step2.guestConfirmedTitle') }}
                  </div>
                  <v-btn
                    text
                    small
                    color="primary"
                    @click="unlockGuest(tabIdx)"
                  >
                    {{ $t('preCheckIn.step2.updateGuest') }}
                  </v-btn>
                </v-card>
                <template v-else>
                  <div class="mb-4">
                    <v-chip
                      v-if="guest.isMain"
                      color="primary"
                      outlined
                      small
                    >
                      <v-icon
                        left
                        x-small
                      >
                        mdi-star
                      </v-icon>{{ $t('preCheckIn.step2.mainGuestChip') }}
                    </v-chip>
                    <v-chip
                      v-else-if="guest.type === 'adult'"
                      color="grey darken-1"
                      outlined
                      small
                    >
                      {{ $t('preCheckIn.step2.additionalAdultChip') }}
                    </v-chip>
                    <v-chip
                      v-else
                      color="teal"
                      outlined
                      small
                    >
                      {{ $t('preCheckIn.step2.childChip') }}
                    </v-chip>
                  </div>

                  <!-- Tipo + Documento -->
                  <v-row no-gutters>
                    <v-col
                      cols="12"
                      md="4"
                      class="pr-md-2"
                    >
                      <v-select
                        :value="guest.type_doc"
                        :items="guest.isMain ? mainDocTypeItems : typeDocItems"
                        item-text="text"
                        item-value="value"
                        outlined
                        :label="fl(guest, 'type_doc', $t('preCheckIn.step2.fields.typeDoc'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].type_doc"
                        @input="val => { setGuest(tabIdx, 'type_doc', val); setGuest(tabIdx, 'cpf', ''); setGuest(tabIdx, 'doc', ''); setGuest(tabIdx, 'foreigner', val !== 'CPF') }"
                        @focus="clearError(tabIdx, 'type_doc')"
                      ></v-select>
                    </v-col>
                    <v-col
                      cols="12"
                      md="8"
                    >
                      <v-text-field
                        v-if="guest.type_doc === 'CPF' || !guest.type_doc"
                        v-mask="'###.###.###-##'"
                        :value="guest.cpf"
                        outlined
                        :label="guest.type === 'child' ? $t('preCheckIn.step2.fields.cpfOptional') : fl(guest, 'cpf', $t('preCheckIn.step2.fields.cpf'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].cpf"
                        @input="val => { setGuest(tabIdx, 'cpf', val); onCpfChange(tabIdx, val) }"
                        @focus="clearError(tabIdx, 'cpf')"
                      ></v-text-field>
                      <v-text-field
                        v-else
                        :value="guest.doc"
                        outlined
                        :label="fl(guest, 'doc', $t('preCheckIn.step2.fields.docNumber'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].doc"
                        @input="val => setGuest(tabIdx, 'doc', val)"
                        @focus="clearError(tabIdx, 'doc')"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Nome -->
                  <v-row
                    no-gutters
                    class="mt-n2"
                  >
                    <v-col
                      cols="12"
                      md="6"
                      class="pr-md-2"
                    >
                      <v-text-field
                        :value="guest.first_name"
                        outlined
                        :label="fl(guest, 'first_name', $t('preCheckIn.step2.fields.firstName'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].first_name"
                        @input="val => setGuestFirstName(tabIdx, val)"
                        @focus="clearError(tabIdx, 'first_name')"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                    >
                      <v-text-field
                        :value="guest.last_name"
                        outlined
                        :label="fl(guest, 'last_name', $t('preCheckIn.step2.fields.lastName'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].last_name"
                        @input="val => setGuestLastName(tabIdx, val)"
                        @focus="clearError(tabIdx, 'last_name')"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Data nascimento + Sexo + Nacionalidade -->
                  <v-row
                    no-gutters
                    class="mt-n2"
                  >
                    <v-col
                      cols="12"
                      md="4"
                      class="pr-md-2"
                    >
                      <v-text-field
                        :value="guest.date_of_birth"
                        outlined
                        :label="fl(guest, 'date_of_birth', $t('preCheckIn.step2.fields.birthDate'))"
                        type="date"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].date_of_birth"
                        @input="val => setGuest(tabIdx, 'date_of_birth', val)"
                        @focus="clearError(tabIdx, 'date_of_birth')"
                      ></v-text-field>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                      class="px-md-2"
                    >
                      <v-select
                        :value="guest.gender"
                        :items="genderItems"
                        item-text="text"
                        item-value="value"
                        outlined
                        :label="fl(guest, 'gender', $t('preCheckIn.step2.fields.gender'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].gender"
                        @input="val => setGuest(tabIdx, 'gender', val)"
                        @focus="clearError(tabIdx, 'gender')"
                      ></v-select>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                    >
                      <v-select
                        :value="guest.nationality_country_id"
                        :items="countryItems"
                        item-value="id"
                        item-text="name"
                        outlined
                        :label="fl(guest, 'nationality_country_id', $t('preCheckIn.step2.fields.nationality'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].nationality_country_id"
                        @input="val => setGuest(tabIdx, 'nationality_country_id', val)"
                        @focus="clearError(tabIdx, 'nationality_country_id')"
                      ></v-select>
                    </v-col>
                  </v-row>

                  <!-- Campos para todos os adultos -->
                  <template v-if="guest.type === 'adult'">
                    <v-row
                      no-gutters
                      class="mt-n2"
                    >
                      <v-col
                        cols="12"
                        md="4"
                        class="pr-md-2"
                      >
                        <v-select
                          :value="guest.marital_status"
                          :items="maritalItems"
                          item-text="text"
                          item-value="value"
                          outlined
                          :label="fl(guest, 'marital_status', $t('preCheckIn.step2.fields.maritalStatus'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].marital_status"
                          @input="val => setGuest(tabIdx, 'marital_status', val)"
                          @focus="clearError(tabIdx, 'marital_status')"
                        ></v-select>
                      </v-col>
                      <v-col
                        cols="12"
                        md="4"
                        class="px-md-2"
                      >
                        <v-text-field
                          :value="guest.profession"
                          outlined
                          :label="fl(guest, 'profession', $t('preCheckIn.step2.fields.profession'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].profession"
                          @input="val => setGuest(tabIdx, 'profession', val)"
                          @focus="clearError(tabIdx, 'profession')"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        cols="12"
                        md="4"
                      >
                        <v-text-field
                          :value="guest.origin"
                          outlined
                          :label="fl(guest, 'origin', $t('preCheckIn.step2.fields.originCity'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].origin"
                          @input="val => setGuest(tabIdx, 'origin', val)"
                          @focus="clearError(tabIdx, 'origin')"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row
                      v-if="guest.type_doc === 'RG'"
                      no-gutters
                      class="mt-n2"
                    >
                      <v-col
                        cols="12"
                        md="6"
                        class="pr-md-2"
                      >
                        <v-text-field
                          :value="guest.org_exp"
                          outlined
                          :label="`${$t('preCheckIn.step2.fields.issuingBody')} *`"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].org_exp"
                          @input="val => setGuest(tabIdx, 'org_exp', val)"
                          @focus="clearError(tabIdx, 'org_exp')"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-divider class="my-3"></v-divider>
                    <div class="text-subtitle-2 mb-2 grey--text text--darken-1">
                      {{ $t('preCheckIn.step2.addressSection') }}
                    </div>
                    <v-row no-gutters>
                      <v-col
                        cols="12"
                        md="3"
                        class="pr-md-2"
                      >
                        <v-text-field
                          v-if="guest.foreigner"
                          :value="guest.zipcode"
                          outlined
                          :label="fl(guest, 'zipcode', $t('preCheckIn.step2.fields.zipcodePostal'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].zipcode"
                          @input="val => setGuest(tabIdx, 'zipcode', val)"
                          @focus="clearError(tabIdx, 'zipcode')"
                        ></v-text-field>
                        <v-text-field
                          v-else
                          v-mask="'#####-###'"
                          :value="guest.zipcode"
                          outlined
                          :label="fl(guest, 'zipcode', $t('preCheckIn.step2.fields.zipcode'))"
                          :loading="loadingCep[tabIdx]"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].zipcode"
                          @input="val => { setGuest(tabIdx, 'zipcode', val); getCEP(tabIdx, val) }"
                          @focus="clearError(tabIdx, 'zipcode')"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        cols="12"
                        md="7"
                        class="px-md-2"
                      >
                        <v-text-field
                          :value="guest.address"
                          outlined
                          :label="fl(guest, 'address', $t('preCheckIn.step2.fields.address'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].address"
                          @input="val => setGuest(tabIdx, 'address', val)"
                          @focus="clearError(tabIdx, 'address')"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        cols="12"
                        md="2"
                      >
                        <v-text-field
                          :value="guest.number"
                          outlined
                          :label="fl(guest, 'number', $t('preCheckIn.step2.fields.number'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].number"
                          @input="val => setGuest(tabIdx, 'number', val)"
                          @focus="clearError(tabIdx, 'number')"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row
                      no-gutters
                      class="mt-n2"
                    >
                      <v-col
                        cols="12"
                        md="4"
                        class="pr-md-2"
                      >
                        <v-text-field
                          :value="guest.complement"
                          outlined
                          :label="$t('preCheckIn.step2.fields.complement')"
                          hide-details
                          @input="val => setGuest(tabIdx, 'complement', val)"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        cols="12"
                        md="8"
                      >
                        <v-text-field
                          :value="guest.neighborhood"
                          outlined
                          :label="fl(guest, 'neighborhood', $t('preCheckIn.step2.fields.neighborhood'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].neighborhood"
                          @input="val => setGuest(tabIdx, 'neighborhood', val)"
                          @focus="clearError(tabIdx, 'neighborhood')"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row
                      no-gutters
                      class="mt-n2"
                    >
                      <v-col
                        cols="12"
                        md="3"
                        class="pr-md-2"
                      >
                        <v-select
                          :value="guest.country_id"
                          :items="countryItems"
                          item-value="id"
                          item-text="name"
                          outlined
                          :label="fl(guest, 'country_id', $t('preCheckIn.step2.fields.country'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].country_id"
                          @input="val => setGuest(tabIdx, 'country_id', val)"
                          @focus="clearError(tabIdx, 'country_id')"
                        ></v-select>
                      </v-col>
                      <v-col
                        v-if="!guest.foreigner"
                        cols="12"
                        md="4"
                        class="px-md-2"
                      >
                        <v-select
                          :value="guest.state_id"
                          :items="stateItems"
                          item-value="id"
                          item-text="name"
                          outlined
                          :label="fl(guest, 'state_id', $t('preCheckIn.step2.fields.state'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].state_id"
                          @input="val => onStateChange(tabIdx, val)"
                          @focus="clearError(tabIdx, 'state_id')"
                        ></v-select>
                      </v-col>
                      <v-col
                        v-if="!guest.foreigner"
                        cols="12"
                        md="5"
                      >
                        <v-select
                          :value="guest.city_id"
                          :items="cityItems[tabIdx] || []"
                          item-value="id"
                          item-text="name"
                          outlined
                          :label="fl(guest, 'city_id', $t('preCheckIn.step2.fields.city'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].city_id"
                          @input="val => setGuest(tabIdx, 'city_id', val)"
                          @focus="clearError(tabIdx, 'city_id')"
                        ></v-select>
                      </v-col>
                    </v-row>

                    <v-divider class="my-3"></v-divider>
                    <div class="text-subtitle-2 mb-2 grey--text text--darken-1">
                      {{ $t('preCheckIn.step2.contactSection') }}
                    </div>
                    <v-row no-gutters>
                      <v-col
                        cols="12"
                        md="6"
                        class="pr-md-2"
                      >
                        <v-text-field
                          :value="guest.email"
                          outlined
                          :label="fl(guest, 'email', $t('preCheckIn.step2.fields.email'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].email"
                          @input="val => setGuest(tabIdx, 'email', val.toLowerCase())"
                          @focus="clearError(tabIdx, 'email')"
                        ></v-text-field>
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-text-field
                          v-if="guest.foreigner"
                          :value="guest.cell_phone"
                          outlined
                          :label="fl(guest, 'cell_phone', $t('preCheckIn.step2.fields.cellPhone'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].cell_phone"
                          @input="val => setGuest(tabIdx, 'cell_phone', val)"
                          @focus="clearError(tabIdx, 'cell_phone')"
                        ></v-text-field>
                        <v-text-field
                          v-else
                          v-mask="'(##)#####-####'"
                          :value="guest.cell_phone"
                          outlined
                          :label="fl(guest, 'cell_phone', $t('preCheckIn.step2.fields.cellPhone'))"
                          :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].cell_phone"
                          @input="val => setGuest(tabIdx, 'cell_phone', val)"
                          @focus="clearError(tabIdx, 'cell_phone')"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </template>

                  <!-- Dados FNRH (todos os hóspedes) -->
                  <v-divider class="my-3"></v-divider>
                  <div class="text-subtitle-2 mb-2 grey--text text--darken-1">
                    {{ $t('preCheckIn.step2.fnrhSection') }}
                  </div>
                  <v-row no-gutters>
                    <v-col
                      cols="12"
                      md="6"
                      class="pr-md-2"
                    >
                      <v-select
                        :value="guest.race"
                        :items="raceItems"
                        item-text="text"
                        item-value="value"
                        outlined
                        :label="fl(guest, 'race', $t('preCheckIn.step2.fields.race'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].race"
                        @input="val => setGuest(tabIdx, 'race', val)"
                        @focus="clearError(tabIdx, 'race')"
                      ></v-select>
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                    >
                      <v-select
                        :value="guest.disability_option"
                        :items="disabilityOptions"
                        item-text="text"
                        item-value="value"
                        outlined
                        :label="fl(guest, 'disability_option', $t('preCheckIn.step2.fields.disability'))"
                        :error-messages="guestErrors[tabIdx] && guestErrors[tabIdx].disability_option"
                        @input="val => setGuest(tabIdx, 'disability_option', val)"
                        @focus="clearError(tabIdx, 'disability_option')"
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row
                    v-if="guest.disability_option && guest.disability_option !== 'Nenhuma'"
                    no-gutters
                    class="mt-n2"
                  >
                    <v-col cols="12">
                      <v-text-field
                        :value="guest.disability_type"
                        outlined
                        :label="$t('preCheckIn.step2.fields.disabilityDesc')"
                        @input="val => setGuest(tabIdx, 'disability_type', val)"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider class="my-4"></v-divider>
                  <v-row
                    justify="end"
                    no-gutters
                  >
                    <v-col cols="auto">
                      <v-btn
                        color="primary"
                        :loading="savingGuest[tabIdx]"
                        @click="saveGuest(tabIdx)"
                      >
                        {{ guest.status === 'completed' ? $t('preCheckIn.step2.updateGuest') : $t('preCheckIn.step2.saveGuest') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
              </v-tab-item>
            </v-tabs-items>

            <!-- Navegação -->
            <v-row class="mt-4">
              <v-col>
                <v-btn
                  text
                  @click="prevTab"
                >
                  {{ activeTab === 0 ? $t('preCheckIn.step2.back') : $t('preCheckIn.step2.previous') }}
                </v-btn>
              </v-col>
              <v-col class="text-right">
                <v-btn
                  v-if="activeTab < guests.length - 1"
                  text
                  color="primary"
                  @click="nextTab"
                >
                  {{ $t('preCheckIn.step2.next') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-stepper-content>

        <!-- ── STEP 3: Sucesso ───────────────────────────────── -->
        <v-stepper-content step="3">
          <v-card
            flat
            class="pa-6 text-center"
          >
            <v-icon
              color="success"
              size="80"
              class="mb-4"
            >
              mdi-check-circle-outline
            </v-icon>
            <div class="text-h5 mb-2">
              {{ $t('preCheckIn.step3.title') }}
            </div>
            <div class="text-body-1 grey--text mb-6">
              {{ $t('preCheckIn.step3.body', { id: reserve ? reserve.id : '' }) }}
            </div>
            <v-divider class="mb-4"></v-divider>
            <div class="text-left mb-2">
              <strong>{{ $t('preCheckIn.step3.registeredGuests') }}</strong>
            </div>
            <v-list dense>
              <v-list-item
                v-for="(g, i) in guests"
                :key="i"
              >
                <v-list-item-icon>
                  <v-icon :color="g.type === 'child' ? 'teal' : 'primary'">
                    {{ g.type === 'child' ? 'mdi-account-child' : 'mdi-account' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ g.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="g.isMain">
                    {{ $t('preCheckIn.step3.mainGuestLabel') }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-stepper-content>
      </v-stepper>
    </v-container>
  </v-app>
</template>

<script>
import { mapActions } from 'vuex'
import axios from 'axios'
import { loadLanguageAsync } from '@/plugins/i18n'

const REQUIRED_MAIN = [
  'first_name', 'last_name', 'cpf', 'date_of_birth', 'gender', 'nationality_country_id',
  'marital_status', 'profession', 'origin', 'type_doc',
  'address', 'number', 'neighborhood', 'zipcode', 'country_id', 'state_id', 'city_id',
  'email', 'cell_phone', 'race', 'disability_option',
]
const REQUIRED_FNRH = ['first_name', 'last_name', 'cpf', 'date_of_birth', 'gender', 'nationality_country_id', 'race', 'disability_option']
const REQUIRED_CHILD = ['first_name', 'last_name', 'date_of_birth', 'gender', 'race', 'disability_option']

// mapeia campo do formulário -> chave de tradução em preCheckIn.step2.fields
const FIELD_LABEL_KEYS = {
  name: 'name',
  first_name: 'firstName',
  last_name: 'lastName',
  cpf: 'cpf',
  date_of_birth: 'birthDate',
  gender: 'gender',
  nationality_country_id: 'nationality',
  marital_status: 'maritalStatus',
  profession: 'profession',
  origin: 'originCity',
  doc: 'docNumber',
  type_doc: 'typeDoc',
  org_exp: 'issuingBody',
  address: 'address',
  number: 'number',
  neighborhood: 'neighborhood',
  zipcode: 'zipcode',
  country_id: 'country',
  state_id: 'state',
  city_id: 'city',
  email: 'email',
  cell_phone: 'cellPhone',
  race: 'race',
  disability_option: 'disability',
}

export default {
  name: 'PreCheckIn',

  data: () => ({
    step: 1,
    locale: 'pt',
    reserveId: '',
    reserveError: '',
    reserve: null,
    guests: [],
    guestErrors: [],
    activeTab: 0,
    cityItems: {},
    loadingCep: {},
    countryItems: [],
    stateItems: [],
    loadingReserve: false,
    savingGuest: {},
    unlockedTabs: {},
    pollTimer: null,

    // valores canônicos enviados ao backend (sempre em PT, independente do idioma exibido)
    genderValues: ['M', 'F'],
    maritalValues: ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'],
    mainDocTypeValues: ['CPF', 'Passaporte', 'RNE'],
    typeDocValues: ['CPF', 'RG', 'CNH', 'Passaporte', 'RNE', 'Certidão de Nascimento', 'Outro'],
    raceValues: ['Branca', 'Preta', 'Parda', 'Amarela', 'Indígena', 'Não Declarada'],
    disabilityValues: ['Nenhuma', 'Física', 'Visual', 'Auditiva', 'Intelectual', 'Múltipla'],
  }),

  computed: {
    genderItems() {
      return this.genderValues.map(value => ({ value, text: this.$t(`preCheckIn.options.gender.${value}`) }))
    },
    maritalItems() {
      return this.maritalValues.map((value, i) => ({ value, text: this.$t(`preCheckIn.options.marital.${i}`) }))
    },
    mainDocTypeItems() {
      return this.mainDocTypeValues.map((value, i) => ({ value, text: this.$t(`preCheckIn.options.mainDocTypes.${i}`) }))
    },
    typeDocItems() {
      return this.typeDocValues.map((value, i) => ({ value, text: this.$t(`preCheckIn.options.typeDoc.${i}`) }))
    },
    raceItems() {
      return this.raceValues.map((value, i) => ({ value, text: this.$t(`preCheckIn.options.race.${i}`) }))
    },
    disabilityOptions() {
      return this.disabilityValues.map((value, i) => ({ value, text: this.$t(`preCheckIn.options.disability.${i}`) }))
    },
  },

  created() {
    this.setLocale(localStorage.getItem('precheckin_locale') || 'pt')
    this.$store.dispatch('noauth/get_countries').then(r => { this.countryItems = r.data })
    this.$store.dispatch('noauth/get_states').then(r => { this.stateItems = r.data })
  },

  beforeDestroy() {
    this.stopPolling()
  },

  methods: {
    setLocale(code) {
      loadLanguageAsync(code).then(() => {
        this.locale = code
        this.$vuetify.lang.current = code
        localStorage.setItem('precheckin_locale', code)
      })
    },

    ...mapActions('noauth', ['get_reserve_info', 'submit_guest_precheckin', 'isRegistered']),

    formatDate(date) {
      if (!date) return ''
      const [y, m, d] = date.split('-')

      return `${d}/${m}/${y}`
    },

    nl2br(text) {
      if (!text) return ''

      return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    },

    guestLabel(guest, i) {
      if (guest.isMain) return this.$t('preCheckIn.guestLabel.main')
      if (guest.type === 'child') {
        const n = this.guests.slice(0, i + 1).filter(g => g.type === 'child').length

        return this.$t('preCheckIn.guestLabel.child', { n })
      }
      const n = this.guests.slice(0, i + 1).filter(g => g.type === 'adult').length

      return this.$t('preCheckIn.guestLabel.adult', { n })
    },

    emptyGuest() {
      return {
        id: null,
        name: '',
        first_name: '',
        last_name: '',
        cpf: '',
        date_of_birth: '',
        gender: '',
        nationality_country_id: '',
        marital_status: '',
        profession: '',
        origin: '',
        doc: '',
        type_doc: 'CPF',
        org_exp: '',
        address: '',
        number: '',
        complement: '',
        neighborhood: '',
        zipcode: '',
        country_id: '',
        state_id: '',
        city_id: '',
        email: '',
        cell_phone: '',
        race: '',
        disability_option: '',
        disability_type: '',
        foreigner: false,
      }
    },

    loadReserve() {
      if (!this.reserveId) {
        this.reserveError = this.$t('preCheckIn.step1.errorEmpty')

        return
      }
      this.loadingReserve = true
      this.get_reserve_info({ reserve_id: this.reserveId })
        .then(({ data }) => {
          if (!data.status) {
            this.reserveError = data.message

            return
          }
          this.reserve = data.data
          this.initForms(data.data)
          this.step = 2
        })
        .catch(err => {
          this.reserveError = err.response?.data?.message || this.$t('preCheckIn.step1.errorNotFound')
        })
        .finally(() => { this.loadingReserve = false })
    },

    buildGuestFromSlot(slot) {
      const splitName = name => {
        const [firstName, ...rest] = (name || '').split(' ')

        return { first_name: firstName || '', last_name: rest.join(' ') }
      }
      const guestBase = slot.guest ? { ...slot.guest, ...splitName(slot.guest.name) } : {}

      return {
        ...this.emptyGuest(),
        ...guestBase,
        position: slot.position,
        type: slot.type,
        isMain: slot.is_main,
        status: slot.status,
        completed_at: slot.completed_at,
      }
    },

    initForms(reserve) {
      this.guests = (reserve.guest_slots || []).map(slot => this.buildGuestFromSlot(slot))
      this.guestErrors = this.guests.map(() => ({}))
      this.savingGuest = {}
      this.unlockedTabs = {}
      this.cityItems = {}
      this.loadingCep = {}

      const firstPending = this.guests.findIndex(g => g.status !== 'completed')
      this.activeTab = firstPending === -1 ? 0 : firstPending

      this.guests.forEach((g, i) => {
        if (g.state_id) this.loadCities(i, g.state_id)
      })

      this.startPolling()
    },

    setGuest(idx, key, val) {
      this.$set(this.guests, idx, { ...this.guests[idx], [key]: val })
    },

    capitalizeName(val) {
      return val.split(' ').map(w => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : '')).join(' ')
    },

    setGuestFirstName(idx, val) {
      const formatted = this.capitalizeName(val)
      this.$set(this.guests, idx, {
        ...this.guests[idx],
        first_name: formatted,
        name: `${formatted} ${this.guests[idx].last_name || ''}`.trim(),
      })
    },

    setGuestLastName(idx, val) {
      const formatted = this.capitalizeName(val)
      this.$set(this.guests, idx, {
        ...this.guests[idx],
        last_name: formatted,
        name: `${this.guests[idx].first_name || ''} ${formatted}`.trim(),
      })
    },

    clearError(idx, key) {
      const errs = { ...this.guestErrors[idx] }
      delete errs[key]
      this.$set(this.guestErrors, idx, errs)
    },

    loadCities(guestIdx, stateId) {
      const state = this.stateItems.find(s => s.id === stateId)
      if (!state) return
      this.$store.dispatch('noauth/get_cities', state.code).then(r => {
        this.$set(this.cityItems, guestIdx, r.data)
      })
    },

    onStateChange(idx, stateId) {
      this.setGuest(idx, 'state_id', stateId)
      this.setGuest(idx, 'city_id', '')
      this.$set(this.cityItems, idx, [])
      this.loadCities(idx, stateId)
    },

    getCEP(idx, zipcode) {
      const raw = (zipcode || '').replace(/\D/g, '')
      if (raw.length !== 8) return
      this.$set(this.loadingCep, idx, true)
      axios.get(`https://viacep.com.br/ws/${raw}/json/`)
        .then(({ data }) => {
          if (data.erro) return
          this.setGuest(idx, 'address', data.logradouro)
          this.setGuest(idx, 'neighborhood', data.bairro)
          this.setGuest(idx, 'country_id', 31)
          const state = this.stateItems.find(s => s.acronym === data.uf)
          if (state) {
            this.setGuest(idx, 'state_id', state.id)
            this.$store.dispatch('noauth/get_cities', state.code).then(r => {
              this.$set(this.cityItems, idx, r.data)
              const city = r.data.find(c => c.name === data.localidade)
              if (city) this.setGuest(idx, 'city_id', city.id)
            })
          }
        })
        .catch(() => {})
        .finally(() => { this.$set(this.loadingCep, idx, false) })
    },

    onCpfChange(idx, cpf) {
      const raw = (cpf || '').replace(/\D/g, '')
      if (raw.length !== 11) return
      this.isRegistered({ cpf }).then(({ data }) => {
        if (data.status && data.data) {
          const current = this.guests[idx]
          const fullName = data.data.name || ''
          const [firstName, ...rest] = fullName.split(' ')
          this.$set(this.guests, idx, {
            ...current,
            ...data.data,
            first_name: firstName || '',
            last_name: rest.join(' '),
            type_doc: current.type_doc || 'CPF',
            isMain: current.isMain,
            type: current.type,
            incomplete_registration: !current.isMain,
          })
          if (data.data.state_id) this.loadCities(idx, data.data.state_id)
        }
      })
    },

    requiredFields(guest) {
      const useCpf = !guest.type_doc || guest.type_doc === 'CPF'
      const needOrg = guest.type_doc === 'RG'
      const swap = fields => {
        let result = fields.map(f => (f === 'cpf' ? (useCpf ? 'cpf' : 'doc') : f))
        if (needOrg && !result.includes('org_exp')) result.push('org_exp')
        if (guest.foreigner) result = result.filter(f => !['state_id', 'city_id'].includes(f))
        else result = result.filter(f => f !== 'nationality_country_id')

        return result
      }
      if (guest.isMain) return swap(REQUIRED_MAIN)
      if (guest.type === 'child') return REQUIRED_CHILD

      return swap(REQUIRED_FNRH)
    },

    // Retorna o label com '*' se o campo for obrigatório para o hóspede
    fl(guest, field, label) {
      return this.requiredFields(guest).includes(field) ? `${label} *` : label
    },

    validateGuest(tabIdx) {
      const guest = this.guests[tabIdx]
      const required = this.requiredFields(guest)
      const errs = {}
      required.forEach(f => {
        if (!guest[f]) {
          const labelKey = FIELD_LABEL_KEYS[f]
          const label = labelKey ? this.$t(`preCheckIn.step2.fields.${labelKey}`) : f
          errs[f] = this.$t('preCheckIn.step2.fieldRequired', { field: label })
        }
      })
      this.$set(this.guestErrors, tabIdx, errs)

      return Object.keys(errs).length === 0
    },

    nextTab() {
      if (this.activeTab < this.guests.length - 1) this.activeTab++
    },

    prevTab() {
      if (this.activeTab === 0) {
        this.step = 1
      } else {
        this.activeTab--
      }
    },

    isTabLocked(idx) {
      const g = this.guests[idx]

      return !!g && g.status === 'completed' && !this.unlockedTabs[idx]
    },

    unlockGuest(idx) {
      this.$set(this.unlockedTabs, idx, true)
    },

    saveGuest(tabIdx) {
      if (!this.validateGuest(tabIdx)) {
        this.$toast.error(this.$t('preCheckIn.step2.requiredError'))

        return
      }
      const {
        isMain, status, completed_at: completedAt, ...payload
      } = this.guests[tabIdx]

      this.$set(this.savingGuest, tabIdx, true)
      this.submit_guest_precheckin({ reserve_id: this.reserve.id, ...payload })
        .then(({ data }) => {
          if (!data.status) {
            this.$toast.error(data.message || this.$t('preCheckIn.genericSubmitError'))

            return
          }
          this.$set(this.guests, tabIdx, {
            ...this.guests[tabIdx],
            status: 'completed',
            completed_at: new Date().toISOString(),
          })
          this.$set(this.unlockedTabs, tabIdx, false)
          this.reserve.completed_guests = data.completed_guests
          this.reserve.total_guests = data.total_guests

          if (data.all_completed) {
            this.reserve.pre_checkin_done = true
            this.stopPolling()
            this.step = 3
          } else {
            this.$toast.success(data.message)
            const nextPending = this.guests.findIndex((g, i) => i !== tabIdx && g.status !== 'completed')
            if (nextPending !== -1) this.activeTab = nextPending
          }
        })
        .catch(err => {
          this.$toast.error(err.response?.data?.message || this.$t('preCheckIn.genericSubmitError'))
        })
        .finally(() => { this.$set(this.savingGuest, tabIdx, false) })
    },

    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(this.pollStatus, 8000)
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    pollStatus() {
      if (!this.reserve || this.step !== 2) return
      this.get_reserve_info({ reserve_id: this.reserve.id })
        .then(({ data }) => {
          if (!data.status) return
          this.reserve.completed_guests = data.data.completed_guests
          this.reserve.total_guests = data.data.total_guests
          this.reserve.pre_checkin_done = data.data.pre_checkin_done

          data.data.guest_slots.forEach((slot, idx) => {
            const local = this.guests[idx]
            if (!local || local.status === 'completed' || idx === this.activeTab) return
            if (slot.status === 'completed') {
              this.$set(this.guests, idx, this.buildGuestFromSlot(slot))
            }
          })

          if (data.data.pre_checkin_done) {
            this.stopPolling()
            this.step = 3
          }
        })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
.v-stepper__content {
  padding: 16px 16px 8px;
}

.rule-card {
  border-radius: 8px;
  border-left: 4px solid;
}

.rule-smoking {
  background-color: #fff5f5;
  border-color: #e53935;
}

.rule-info {
  background-color: #f0f7ff;
  border-color: #1e88e5;
}

.rule-icon-wrap {
  line-height: 1;
  padding-top: 2px;
}
</style>
